/**
 * @fileoverview Portaled column properties editor — Content | Style tabs for the
 *             selected report column (mirrors the document editor's band panel).
 * @module ui-admin/components/ReportCanvasBuilder/CanvasColumnPropertiesPanel
 *
 * Responsibilities: - Edit one column's label, visibility, format, width,
 *                     sort order, aggregation, and parameter binding (Content
 *                     tab); edit its visual overrides via ElementStyleControls
 *                     + background/border (Style tab).
 * Dependencies:     - React, @firesquirrel/local-db (ReportColumn types),
 *                     @firesquirrel/core-domain (ReportParameter for binding),
 *                     ../template-builder/documentSectionEditors (ElementStyleControls).
 * Architecture:     - Presentation-only, controlled — emits patched columns via
 *                     onChange; rendered either inside the host's right-pane
 *                     properties tab (portal) or as a fixed overlay by the
 *                     parent canvas.
 * Notes:            - The style is stored on column.style (ReportColumnStyle),
 *                     a superset of the document-render ElementStyle shape plus
 *                     background/border for the report table cell.
 */

import React from 'react';
import { X } from 'lucide-react';
import type { ReportColumn, ReportColumnStyle, ReportParameter } from '@firesquirrel/local-db';
import { ElementStyleControls } from '../template-builder/documentSectionEditors/ElementStyleControls';

/**
 * @contract CanvasColumnPropertiesPanelProps
 * Purpose: Props for the column properties editor.
 * Fields:
 * - column: the selected ReportColumn (controlled).
 * - parameters: declared report parameters (for the binding dropdown).
 * - onChange: emit a patched ReportColumn.
 * - onClose: deselect (clears the portaled panel).
 */
export interface CanvasColumnPropertiesPanelProps {
  column: ReportColumn;
  parameters: ReportParameter[];
  onChange: (next: ReportColumn) => void;
  onClose: () => void;
}

const inputCls =
  'w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 focus:border-orange-400 focus:outline-none';
const labelCls = 'mb-1 block text-[11px] font-medium text-gray-500';

const FORMAT_OPTIONS: Array<{ value: NonNullable<ReportColumn['format']>; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'date', label: 'Date (MM/DD/YYYY)' },
];

/**
 * @component CanvasColumnPropertiesPanel
 * Purpose:     Edit a single report column's content + style properties.
 * Props:       CanvasColumnPropertiesPanelProps.
 * Behavior:    Content | Style sub-tabs; every edit emits a patched column via
 *              onChange (controlled). Parameter binding section only shows when
 *              the report declares parameters.
 * Data Source: none (controlled).
 * Constraints: presentation-only.
 */
export function CanvasColumnPropertiesPanel({
  column, parameters, onChange, onClose,
}: CanvasColumnPropertiesPanelProps) {
  const [tab, setTab] = React.useState<'content' | 'style'>('content');

  const patch = (next: Partial<ReportColumn>) => onChange({ ...column, ...next });
  const patchStyle = (next: Partial<ReportColumnStyle>) => {
    const merged: ReportColumnStyle = { ...(column.style ?? {}), ...next };
    (Object.keys(merged) as Array<keyof ReportColumnStyle>).forEach((k) => {
      if (merged[k] === undefined || merged[k] === '') delete merged[k];
    });
    patch({ style: Object.keys(merged).length ? merged : undefined });
  };

  return (
    <div className="flex h-full flex-col" data-testid="column-properties-panel">
      <header className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
        <span className="text-sm font-semibold text-gray-800">Column · {column.label}</span>
        <button type="button" aria-label="Close properties" onClick={onClose}
          className="rounded-sm p-1 text-gray-400 hover:bg-gray-100">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="px-3 pt-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" aria-label="Column visible" checked={column.visible !== false}
            onChange={(e) => patch({ visible: e.target.checked })} />
          Enabled (visible on report)
        </label>
      </div>

      <div className="mt-2 flex border-b border-gray-200 px-3" role="tablist" aria-label="Column properties tabs">
        {(['content', 'style'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t}
            data-testid={`col-prop-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-medium capitalize ${tab === t ? 'border-b-2 border-orange-500 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {tab === 'content' && (
          <div className="flex flex-col gap-3">
            <label>
              <span className={labelCls}>Column Label</span>
              <input type="text" aria-label="Column label" value={column.label}
                onChange={(e) => patch({ label: e.target.value })} className={inputCls} />
            </label>

            <div>
              <span className={labelCls}>Source Field (read-only)</span>
              <code className="block rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600">{column.field}</code>
            </div>

            <label>
              <span className={labelCls}>Display Format</span>
              <select aria-label="Display format" value={column.format ?? 'text'}
                onChange={(e) => patch({ format: e.target.value as ReportColumn['format'] })} className={inputCls}>
                {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>

            <label>
              <span className={labelCls}>Column Width (px, optional)</span>
              <input type="number" aria-label="Column width" value={column.width ?? ''}
                onChange={(e) => patch({ width: e.target.value === '' ? undefined : Number(e.target.value) })}
                className={inputCls} />
            </label>

            <label>
              <span className={labelCls}>Sort Order</span>
              <select aria-label="Sort order" value={column.sortOrder === undefined ? '' : String(column.sortOrder)}
                onChange={(e) => patch({ sortOrder: e.target.value === '' ? undefined : Number(e.target.value) })}
                className={inputCls}>
                <option value="">None</option>
                <option value="1">Ascending</option>
                <option value="-1">Descending</option>
              </select>
            </label>

            {parameters.length > 0 && (
              <fieldset className="rounded-md border border-violet-200 bg-violet-50/50 p-2">
                <legend className="px-1 text-[11px] font-medium text-violet-700">Parameter Filter Binding</legend>
                <label className="block">
                  <span className={labelCls}>Driven by Parameter</span>
                  <select
                    aria-label="Driven by parameter"
                    value={column.parameterId ?? ''}
                    onChange={(e) => patch({ parameterId: e.target.value || undefined })}
                    className={inputCls}
                  >
                    <option value="">— none —</option>
                    {parameters.map((p) => (
                      <option key={p.parameterId} value={p.parameterId}>{p.label} ({p.parameterId})</option>
                    ))}
                  </select>
                </label>
                {column.parameterId && (
                  <p className="mt-1 text-[11px] text-violet-700">
                    Overrides the static filter value at run time &amp; schedule time.
                  </p>
                )}
              </fieldset>
            )}
          </div>
        )}

        {tab === 'style' && (
          <div className="flex flex-col gap-3">
            <div>
              <span className={labelCls}>Header / Cell Text Styling</span>
              <ElementStyleControls
                label="column text"
                value={column.style}
                onChange={(style) => patch({ style: style as ReportColumnStyle | undefined })}
              />
            </div>
            <label>
              <span className={labelCls}>Column Background Color</span>
              <input type="color" aria-label="Background color" className="h-8 w-full rounded-md border border-gray-300"
                value={column.style?.backgroundColor ?? '#ffffff'}
                onChange={(e) => patchStyle({ backgroundColor: e.target.value === '#ffffff' ? undefined : e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label>
                <span className={labelCls}>Border Width</span>
                <input type="number" aria-label="Border width" min={0} className={inputCls}
                  value={column.style?.borderWidth ?? 0}
                  onChange={(e) => patchStyle({ borderWidth: Number(e.target.value) || undefined, borderStyle: Number(e.target.value) > 0 ? 'solid' : undefined })} />
              </label>
              <label>
                <span className={labelCls}>Border Color</span>
                <input type="color" aria-label="Border color" className="h-8 w-full rounded-md border border-gray-300"
                  value={column.style?.borderColor ?? '#000000'}
                  onChange={(e) => patchStyle({ borderColor: e.target.value === '#000000' ? undefined : e.target.value })} />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
